import { useSoftphone } from "@dialstack/sdk-react/softphone";
import { useEffect, useRef } from "react";

import { datadogRum } from "@datadog/browser-rum";

// Ghost calls (ENG-2012): the clinic user is done with a call, but it stays
// connected on the patient's phone. DialStack has no server-side hangup, so
// every path that ends a call has to get a `call.hangup` out of THIS client:
//
//   1. A call the platform hands back (`call.restored`) after the phone was
//      replaced mid-call — the SDK does not implement restore, so nothing
//      local owns that call and nobody can hang it up.
//   2. Hang up pressed while the socket is down: `hangup()` throws and the
//      call carries on once the socket reconnects.
//   3. The tab closing or reloading mid-call.

type SoftphoneCall = ReturnType<typeof useSoftphone>["calls"][number];

type PhoneErrorEvent = { code?: string; message?: string; callId?: string };

// The SDK's only signal for a restored call. Pinned against the SDK build by
// the test, together with the private `transport` field used below.
const isRestoredCallError = (error: PhoneErrorEvent) =>
  error.code === "internal_error" &&
  Boolean(error.message?.includes("call.restored"));

type PhoneInternals = {
  transport?: { send: (message: Record<string, string>) => void } | null;
};

const isLive = (call: SoftphoneCall) => call.state !== "ended";
const isUnansweredInbound = (call: SoftphoneCall) =>
  call.direction === "inbound" &&
  (call.state === "ringing" || call.state === "trying");

export default function useGhostCallGuard() {
  const { webrtcPhone, calls, connection, lastError, clearError } =
    useSoftphone();
  const callsRef = useRef(calls);
  callsRef.current = calls;

  useEffect(() => {
    if (!webrtcPhone) return undefined;
    const onError = (error: PhoneErrorEvent) => {
      const { callId } = error;
      if (!callId || !isRestoredCallError(error)) return;
      // The same phone reconnecting mid-call still holds the call — that one
      // is alive and has its own Hang up button.
      if (callsRef.current.some((call) => call.id === callId)) return;
      let hungUp = false;
      try {
        const { transport } = webrtcPhone as unknown as PhoneInternals;
        transport?.send({ type: "call.hangup", call_id: callId });
        hungUp = Boolean(transport);
      } catch {
        // Reported below; there is no second way to reach this call.
      }
      datadogRum.addAction("softphone_orphan_call_restored", {
        feature: "dialstack-softphone",
        callId,
        hungUp
      });
    };
    webrtcPhone.on("error", onError);
    return () => webrtcPhone.off("error", onError);
  }, [webrtcPhone]);

  // `call.hangup` is patched per call rather than inferred from `lastError`:
  // the SDK reports a failed hold or transfer with the same `transport_closed`
  // code and no call id, and retrying the wrong one would end a call the user
  // only meant to hold.
  const patchedCallsRef = useRef(new WeakSet<SoftphoneCall>());
  const failedHangupsRef = useRef(new Set<SoftphoneCall>());
  useEffect(() => {
    calls.forEach((call) => {
      if (patchedCallsRef.current.has(call)) return;
      patchedCallsRef.current.add(call);
      const hangup = call.hangup.bind(call);
      // eslint-disable-next-line no-param-reassign
      call.hangup = () => {
        try {
          hangup();
        } catch (error) {
          failedHangupsRef.current.add(call);
          throw error;
        }
      };
    });
  }, [calls]);

  useEffect(() => {
    if (connection !== "connected" || failedHangupsRef.current.size === 0)
      return;
    const retries = [...failedHangupsRef.current].filter(isLive);
    failedHangupsRef.current.clear();
    if (retries.length === 0) return;
    retries.forEach((call) => {
      try {
        call.hangup();
      } catch {
        // Queued again by the patch above for the next reconnect.
      }
    });
    datadogRum.addAction("softphone_hangup_retried", {
      feature: "dialstack-softphone",
      calls: retries.length
    });
    // The "could not hang up" chip no longer describes anything.
    if (lastError?.code === "transport_closed") clearError();
  }, [clearError, connection, lastError]);

  useEffect(() => {
    // `pagehide`, not `beforeunload`: it only fires once the page is really
    // going, so cancelling an "unsaved changes" prompt cannot cost the call.
    // A call still ringing in is left alone — it can be answered elsewhere.
    const hangUpAll = () =>
      callsRef.current
        .filter((call) => isLive(call) && !isUnansweredInbound(call))
        .forEach((call) => {
          try {
            call.hangup();
          } catch {
            // The socket is already gone; the platform's own timeout applies.
          }
        });
    window.addEventListener("pagehide", hangUpAll);
    return () => window.removeEventListener("pagehide", hangUpAll);
  }, []);
}
